import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useActionBar } from "@/contexts/ActionBarContext";

const DEFAULT_ACTION_BUTTONS = [];
const DEFAULT_TITLE_ACTIONS = [];

/**
 * PageHeader - Normalized header component for all pages
 * 
 * Structure:
 * 1. Action Bar - Buttons are now rendered in AppHeader via ActionBarContext
 * 2. Main Header:
 *    - Left: Icon/Avatar + Overline + Title + Action icons (share, pin, etc.)
 *    - Right: Free zone for additional content
 * 
 * @param {Object} props
 * @param {Array} props.actionBarButtons - Array of button configs for action bar [{icon, label, onClick, variant, testId, tooltip}]
 * @param {string} props.iconType - 'avatar' | 'icon' | 'custom'
 * @param {React.ReactNode} props.icon - Icon component (for iconType='icon')
 * @param {string} props.avatarSrc - Avatar image URL (for iconType='avatar')
 * @param {string} props.avatarFallback - Avatar fallback text
 * @param {string} props.overline - Small text above title (e.g., "CANTO DO MOTARD")
 * @param {string} props.title - Main title text
 * @param {Array} props.titleActions - Array of action buttons next to title [{icon, onClick, tooltip, testId}]
 * @param {React.ReactNode} props.rightContent - Content for the right side (stats, badges, etc.)
 * @param {React.ReactNode} props.freeZone - Content for zone 3 (between title and content)
 * @param {string} props.className - Additional classes for the container
 */
export function PageHeader({
  actionBarButtons = DEFAULT_ACTION_BUTTONS,
  actionBarLeftContent,
  iconType = "icon",
  icon: IconComponent,
  avatarSrc,
  avatarFallback = "M",
  overline,
  title,
  titleActions = DEFAULT_TITLE_ACTIONS,
  rightContent,
  freeZone,
  className = "",
}) {
  const { setContent, clearContent } = useActionBar();

  // Set action bar content when component mounts or buttons change
  useEffect(() => {
    if (actionBarButtons.length > 0 || actionBarLeftContent) {
      const content = (
        <>
          {/* Contextual action content */}
          {actionBarLeftContent && (
            <div className="flex items-center gap-2">
              {actionBarLeftContent}
            </div>
          )}
          {/* Buttons */}
          <TooltipProvider>
            {actionBarButtons.map((btn, idx) => {
              // Determine button styling based on variant
              const isSaveVariant = btn.variant === "save";
              const isActive = isSaveVariant && !btn.disabled;
              
              const buttonClassName = isSaveVariant
                ? `font-bold uppercase tracking-wide text-xs sm:text-sm transition-all ${
                    isActive 
                      ? "bg-orange-500 hover:bg-orange-600 text-white border-orange-500 hover:border-orange-600" 
                      : "bg-muted/50 text-muted-foreground border-muted opacity-50 cursor-not-allowed"
                  }`
                : "action-bar-btn";
              
              return (
                <Tooltip key={idx}>
                  <TooltipTrigger asChild>
                    <Button
                      variant={isSaveVariant ? "default" : "outline"}
                      onClick={btn.onClick}
                      disabled={btn.disabled}
                      className={buttonClassName}
                      data-testid={btn.testId}
                    >
                      {btn.icon && <btn.icon className={`h-4 w-4 sm:mr-1 ${btn.iconClassName || ''}`} />}
                      <span className="btn-label">{btn.label}</span>
                    </Button>
                  </TooltipTrigger>
                  {btn.tooltip && (
                    <TooltipContent>
                      <p>{btn.tooltip}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              );
            })}
          </TooltipProvider>
        </>
      );
      setContent(content);
    }

    // Cleanup on unmount
    return () => clearContent();
  }, [actionBarButtons, actionBarLeftContent, setContent, clearContent]);

  return (
    <div className={className}>
      {/* Main Header Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pt-6 pb-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            {/* Left Side: Icon + Title + Action Icons */}
            <div className="flex items-center gap-4">
              {/* Icon/Avatar (Zone with icon) */}
              {iconType === "avatar" && (
                <Avatar className="h-16 w-16 border-4 border-primary/50 shadow-xl">
                  <AvatarImage src={avatarSrc} alt={title} />
                  <AvatarFallback className="text-xl sm:text-2xl bg-primary/20">
                    {avatarFallback}
                  </AvatarFallback>
                </Avatar>
              )}
              {iconType === "icon" && IconComponent && (
                <div className="h-12 w-12 md:h-16 md:w-16 rounded-full border-4 border-primary/50 shadow-xl bg-card/90 backdrop-blur-sm flex items-center justify-center">
                  <IconComponent className="h-6 w-6 md:h-8 md:w-8 text-primary" />
                </div>
              )}
              {iconType === "custom" && IconComponent}

              {/* Title Section */}
              <div>
                {overline && (
                  <p className="overline mb-1">
                    {overline}
                  </p>
                )}
                <div className="flex items-center gap-3">
                  <h1 className="text-lg sm:text-2xl font-black tracking-tight text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                    {title}
                  </h1>
                  {/* 2. Title Action Icons (share, pin, etc.) */}
                  {titleActions.length > 0 && (
                    <TooltipProvider>
                      <div className="flex items-center gap-2">
                        {titleActions.map((action, idx) => (
                          <Tooltip key={idx}>
                            <TooltipTrigger asChild>
                              {action.component ? (
                                action.component
                              ) : (
                                <button
                                  onClick={action.onClick}
                                  className="h-10 w-10 rounded-full text-white bg-black/40 hover:bg-black/60 flex items-center justify-center transition-colors"
                                  data-testid={action.testId}
                                >
                                  {action.icon && <action.icon className="h-5 w-5" />}
                                </button>
                              )}
                            </TooltipTrigger>
                            {action.tooltip && (
                              <TooltipContent>
                                <p>{action.tooltip}</p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        ))}
                      </div>
                    </TooltipProvider>
                  )}
                </div>
              </div>
            </div>

            {/* Right Side: Stats, badges, etc. */}
            {rightContent && (
              <div className="flex flex-wrap items-center gap-3">
                {rightContent}
              </div>
            )}
          </div>

          {/* 3. Free Zone - For additional content */}
          {freeZone && (
            <div className="mt-4">
              {freeZone}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * StatBadge - Reusable stat badge for header
 */
export function StatBadge({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 bg-card/80 backdrop-blur-sm px-3 py-1.5 rounded-lg">
      {Icon && <Icon className="h-4 w-4 text-primary" />}
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="font-mono font-bold text-sm">{value}</span>
    </div>
  );
}

export default PageHeader;
